import React, { useContext, useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import { CartContext } from '../CartContext';
import CartItem from './CartItem';

export default function ShoppingCartPage({ setPage }) {
  const { cart, updateQuantity, removeFromCart } = useContext(CartContext);
  const [deliveryAddress, setDeliveryAddress] = useState(() => {
    return localStorage.getItem('cusAddress') || '';
  });

  const [deliveryDate, setDeliveryDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  const handleProceed = () => {
    if (!deliveryAddress.trim()) {
      alert('Please enter a delivery address.');
      return;
    }

    localStorage.setItem('deliveryAddress', deliveryAddress);
    localStorage.setItem('deliveryDate', deliveryDate);

    setPage('order');
  };

  if (cart.length === 0) {
    return (
      <div className="text-center py-20">
        <ShoppingBag className="mx-auto h-24 w-24 text-gray-300" />
        <h2 className="mt-4 text-2xl font-semibold mb-2 text-gray-800">
          Your cart is empty
        </h2>
        <p className="text-gray-500 mb-6">
          Looks like you haven't added anything yet.
        </p>
        <button
          onClick={() => setPage('products')}
          className="bg-teal-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-teal-700 transition duration-300 shadow-md hover:shadow-lg"
        >
          Start Shopping
        </button>
      </div>
    );
  }

  const totalQty = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-teal-50 py-4">
      <div className="container mx-auto max-w-5xl px-2">
        {/* Header */}
        <div className="text-center mb-3">
          <h1 className="text-2xl font-extrabold text-gray-800">
            🛒 Your Shopping Cart
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* LEFT SIDE - CART ITEMS */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-6 space-y-3">
            {cart.map(item => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={updateQuantity}
                onRemove={removeFromCart}
              />
            ))}
          </div>

          {/* RIGHT SIDE - ORDER SUMMARY */}
          <div className="bg-white rounded-2xl shadow-xl p-6 space-y-6 h-fit sticky top-24">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-3">
              Order Summary
            </h2>

            {/* Total Items */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Total Products</span>
              <span className="font-semibold text-teal-700 text-lg">{cart.length}</span>
            </div>

            {/* Total Quantity */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Total Quantity</span>
              <span className="font-semibold text-teal-700 text-lg">{totalQty}</span>
            </div>

            {/* Delivery Address */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Delivery Address
              </label>
              <textarea
                rows="3"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 resize-none"
                placeholder="Enter delivery address"
                required
              />
            </div>

            {/* Delivery Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Delivery Date
              </label>
              <input
                type="date"
                value={deliveryDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleProceed}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 
                         hover:from-teal-600 hover:to-emerald-700
                         text-white font-bold py-4 rounded-xl 
                         transition-all duration-300 shadow-lg hover:shadow-2xl"
            >
              Proceed to Checkout →
            </button>

            {/* Continue Shopping */}
            <button
              onClick={() => setPage('products')}
              className="w-full border border-gray-300 py-3 rounded-xl 
                         text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
